import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { track, ANALYTICS_EVENTS } from "../../../utils/productAnalytics";

const headerNav = [
  { label: "Taskyとは", href: "#features" },
  { label: "実績", href: "#gallery" },
  { label: "料金", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

function NavLink({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick}>
      {children}
    </a>
  );
}

const LPHeader: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-sm transition-all duration-300 border-b border-[#E4E0ED] bg-[#F5F4F0]/95"
      style={{ height: 72 }}
    >
      <div className="h-full flex items-center justify-between pl-5 pr-5 md:pl-10 md:pr-10 lg:pl-[120px] lg:pr-[120px]">
        {/* Logo */}
        <Link to="/" className="no-underline">
          <img
            src={`${process.env.PUBLIC_URL}/lp/logos/logo-dark.png`}
            alt="Tasky"
            className="h-9"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {headerNav.map((item) => (
            <NavLink
              key={item.label}
              href={item.href}
              className="text-[15px] font-medium font-['Inter'] text-[#1D1927] no-underline hover:opacity-60 transition-all duration-300"
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA + Mobile Menu */}
        <div className="flex items-center gap-4">
          <Link
            to="/login"
            className="hidden md:inline-flex items-center justify-center text-[15px] font-medium text-[#1D1927] no-underline hover:opacity-60 transition-all duration-300 mr-2"
          >
            ログイン
          </Link>
          <Link
            to="/signup"
            onClick={() => track(ANALYTICS_EVENTS.CTA_CLICKED, { cta_id: "header_signup", location: "landing_header" })}
            className="inline-flex items-center justify-center bg-[#3eb298] text-white text-[13px] md:text-[16px] font-semibold tracking-[0.02em] px-4 md:px-6 py-3 rounded-full min-h-[44px] hover:opacity-80 transition-opacity duration-200 no-underline"
          >
            無料で試す
          </Link>
          <button
            className="lg:hidden text-2xl leading-none p-1 text-[#1D1927]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="メニュー"
          >
            {mobileOpen ? "\u2715" : "\u2630"}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#F5F4F0]/95 backdrop-blur-sm border-b border-[#E4E0ED] overflow-hidden"
          >
            <div className="px-5 md:px-10 py-6 flex flex-col gap-4">
              {headerNav.map((item) => (
                <NavLink
                  key={item.label}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-[18px] font-medium text-[#1D1927] no-underline"
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="text-[18px] font-medium text-[#1D1927] no-underline"
              >
                ログイン
              </Link>
              <Link
                to="/signup"
                onClick={() => {
                  setMobileOpen(false);
                  track(ANALYTICS_EVENTS.CTA_CLICKED, { cta_id: "header_signup_mobile", location: "landing_header" });
                }}
                className="inline-flex items-center justify-center h-12 px-6 rounded-full bg-[#3eb298] text-white text-[16px] font-semibold mt-2 no-underline"
              >
                無料で試す
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default LPHeader;
